const CLAMP = (v, min, max) => Math.max(min, Math.min(max, v));
const lerp = (a, b, t) => a + (b - a) * t;
const norm100 = (v) => CLAMP(v, 0, 100) / 100;

const rarityWeightEntries = [
  ['common', 45],
  ['uncommon', 30],
  ['rare', 16],
  ['epic', 7],
  ['legendary', 2],
];

export const WEAPON_RARITY_WEIGHTS = Object.fromEntries(rarityWeightEntries);

export const WEAPON_CATEGORY = {
  LONG_RANGE: 'long-range',
  CLOSE_RANGE: 'close-range',
  MELEE: 'melee',
  EXPLOSIVE: 'explosive',
};

function toRuntimeStats(def) {
  const fireT = norm100(def.stats.fireRate);
  const reloadT = norm100(def.stats.reload);
  const rangeT = norm100(def.stats.range);
  const accuracyT = norm100(def.stats.accuracy);
  const critT = norm100(def.stats.crit);
  const dmgT = norm100(def.stats.damage);

  const baseDamage = lerp(0.65, 4.6, dmgT);
  const cooldown = lerp(0.95, 0.085, fireT);
  const projectileSpeed = lerp(18, 54, rangeT);
  const projectileLife = lerp(0.32, 1.65, rangeT);
  const spread = lerp(0.42, 0.008, accuracyT);
  const reloadDuration = lerp(2.25, 0.2, reloadT);
  const critChance = lerp(0, 0.55, critT);
  const magSize = Math.round(lerp(3, 34, reloadT));
  const reserve = Math.round(lerp(20, 160, reloadT));

  return {
    baseDamage,
    cooldown,
    projectileSpeed,
    projectileLife,
    spread,
    reloadDuration,
    critChance,
    magSize,
    reserve,
  };
}

function makeWeapon(def) {
  return {
    ...def,
    runtime: toRuntimeStats(def),
  };
}

export const WEAPONS = [
  makeWeapon({
    id: 'kagenuki_sr7',
    name: 'Kagenuki SR-7',
    rarity: 'legendary',
    category: WEAPON_CATEGORY.LONG_RANGE,
    special: 'pierce',
    stats: { damage: 95, fireRate: 15, range: 98, reload: 20, accuracy: 97, crit: 55 },
  }),
  makeWeapon({
    id: 'phantom_arc',
    name: 'Phantom Arc',
    rarity: 'epic',
    category: WEAPON_CATEGORY.LONG_RANGE,
    special: 'coverPenetration',
    stats: { damage: 80, fireRate: 30, range: 90, reload: 35, accuracy: 85, crit: 40 },
  }),
  makeWeapon({
    id: 'ryuu_crossbow',
    name: 'Ryuu Crossbow',
    rarity: 'rare',
    category: WEAPON_CATEGORY.LONG_RANGE,
    special: 'silent',
    stats: { damage: 58, fireRate: 55, range: 80, reload: 60, accuracy: 75, crit: 30 },
  }),
  makeWeapon({
    id: 'stormcaller_smr2',
    name: 'Stormcaller SMR-2',
    rarity: 'uncommon',
    category: WEAPON_CATEGORY.LONG_RANGE,
    special: 'burst3',
    stats: { damage: 45, fireRate: 50, range: 72, reload: 65, accuracy: 65, crit: 18 },
  }),
  makeWeapon({
    id: 'shinkansen_pistol',
    name: 'Shinkansen Pistol',
    rarity: 'epic',
    category: WEAPON_CATEGORY.CLOSE_RANGE,
    special: 'ricochet2',
    stats: { damage: 35, fireRate: 60, range: 50, reload: 75, accuracy: 70, crit: 28 },
  }),
  makeWeapon({
    id: 'reiketsu_shotgun',
    name: 'Reiketsu Shotgun',
    rarity: 'rare',
    category: WEAPON_CATEGORY.CLOSE_RANGE,
    special: 'bleedOnCrit',
    stats: { damage: 72, fireRate: 35, range: 22, reload: 45, accuracy: 40, crit: 35 },
  }),
  makeWeapon({
    id: 'jinsoku_smg',
    name: 'Jinsoku SMG',
    rarity: 'uncommon',
    category: WEAPON_CATEGORY.CLOSE_RANGE,
    special: 'dualWieldTuned',
    stats: { damage: 28, fireRate: 90, range: 30, reload: 70, accuracy: 48, crit: 14 },
  }),
  makeWeapon({
    id: 'akatsuki_ar',
    name: 'Akatsuki AR',
    rarity: 'common',
    category: WEAPON_CATEGORY.CLOSE_RANGE,
    special: 'balanced',
    stats: { damage: 38, fireRate: 65, range: 50, reload: 72, accuracy: 75, crit: 12 },
  }),
  makeWeapon({
    id: 'oni_blade',
    name: 'Oni Blade',
    rarity: 'legendary',
    category: WEAPON_CATEGORY.MELEE,
    special: 'lifesteal',
    stats: { damage: 90, fireRate: 85, range: 8, reload: 100, accuracy: 100, crit: 45 },
  }),
  makeWeapon({
    id: 'tetsu_kusarigama',
    name: 'Tetsu Kusarigama',
    rarity: 'epic',
    category: WEAPON_CATEGORY.MELEE,
    special: 'pullStun',
    stats: { damage: 60, fireRate: 70, range: 20, reload: 100, accuracy: 88, crit: 33 },
  }),
  makeWeapon({
    id: 'goryuu_warhammer',
    name: 'Goryuu Warhammer',
    rarity: 'rare',
    category: WEAPON_CATEGORY.MELEE,
    special: 'slam',
    stats: { damage: 85, fireRate: 42, range: 10, reload: 100, accuracy: 100, crit: 22 },
  }),
  makeWeapon({
    id: 'kaito_shortsword',
    name: 'Kaito Shortsword',
    rarity: 'common',
    category: WEAPON_CATEGORY.MELEE,
    special: 'parryWindow',
    stats: { damage: 35, fireRate: 95, range: 6, reload: 100, accuracy: 100, crit: 20 },
  }),
  makeWeapon({
    id: 'jigoku_launcher',
    name: 'Jigoku Launcher',
    rarity: 'legendary',
    category: WEAPON_CATEGORY.EXPLOSIVE,
    special: 'cluster',
    stats: { damage: 88, fireRate: 28, range: 55, reload: 28, accuracy: 50, crit: 25 },
  }),
  makeWeapon({
    id: 'tenrai_mortar',
    name: 'Tenrai Mortar',
    rarity: 'rare',
    category: WEAPON_CATEGORY.EXPLOSIVE,
    special: 'delayedFuse',
    stats: { damage: 75, fireRate: 18, range: 70, reload: 32, accuracy: 42, crit: 20 },
  }),
  makeWeapon({
    id: 'bakuha_mines',
    name: 'Bakuha Mines',
    rarity: 'uncommon',
    category: WEAPON_CATEGORY.EXPLOSIVE,
    special: 'proximityMine',
    stats: { damage: 65, fireRate: 100, range: 35, reload: 50, accuracy: 60, crit: 15 },
  }),
];

export const WEAPON_BY_ID = Object.fromEntries(WEAPONS.map((w) => [w.id, w]));

export const STARTER_WEAPON_IDS = ['akatsuki_ar', 'kaito_shortsword'];

export function getWeaponById(id) {
  return WEAPON_BY_ID[id] || null;
}

export function getRarityWeight(rarity, { allowLegendary = true } = {}) {
  if (!allowLegendary && rarity === 'legendary') return 0;
  return WEAPON_RARITY_WEIGHTS[rarity] ?? 0;
}

export function pickRandomWeaponId(rng, { allowLegendary = true, category = null } = {}) {
  const pool = category
    ? WEAPONS.filter((w) => w.category === category)
    : WEAPONS.slice();
  let total = 0;
  for (const weapon of pool) total += getRarityWeight(weapon.rarity, { allowLegendary });
  if (total <= 0) return pool[0]?.id ?? null;
  let roll = (rng?.() ?? Math.random()) * total;
  for (const weapon of pool) {
    roll -= getRarityWeight(weapon.rarity, { allowLegendary });
    if (roll <= 0) return weapon.id;
  }
  return pool[pool.length - 1]?.id ?? null;
}

export function rarityColor(rarity) {
  if (rarity === 'legendary') return '#ff9a2b';
  if (rarity === 'epic') return '#d179ff';
  if (rarity === 'rare') return '#5db4ff';
  if (rarity === 'uncommon') return '#7ae96c';
  return '#d8d8d8';
}
